import {
  AVAILABILITY_STATE,
  AvailabilityActionFormUtility,
  BulkAvailabilityBuySideMembersForm,
  BulkAvailabilityMember,
  BulkAvailabilityMembers,
  BulkAvailabilityMembersForm,
  BulkAvailabilityMembersResponse,
  BulkAvailabilityState,
  BulkAvailabilityUpdateMembers,
  BulkFormAvailabilityMember,
} from 'router/subrouters/DataRoom/models/DataRoomBulk';
import {
  DataRoomBuySideGroup,
  DataRoomMember,
  DataRoomMemberRole,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  isDataRoomAdminOrOwner,
  isDataRoomPublisher,
} from 'router/subrouters/DataRoom/utils';
import utils, {
  addDaysToDate,
  getIsNodeFile,
  getShareableChildIds,
  getUserFullNameOrEmail,
  isSingleElementArray,
} from 'utils';

export const MAX_EXPIRATION_DAYS = 180;

export const getIsChecked = (status: BulkAvailabilityState) =>
  status !== AVAILABILITY_STATE.OFF;

export const getIsMixed = (status: BulkAvailabilityState) =>
  status === AVAILABILITY_STATE.MIXED;

export const getNewCheckboxState = (state: BulkAvailabilityState) => {
  if (state === AVAILABILITY_STATE.ON) return AVAILABILITY_STATE.OFF;

  return AVAILABILITY_STATE.ON;
};

export const getAvailabilityState = (value: boolean) =>
  (value
    ? AVAILABILITY_STATE.ON
    : AVAILABILITY_STATE.OFF) as BulkAvailabilityState;

export const getFileExpirationDateInDays = (
  fileExpirationDate: Date,
  accessConfirmationDays?: number,
) => {
  if (!fileExpirationDate) return accessConfirmationDays;

  const { seconds, day } = utils.getDateVariables(fileExpirationDate);

  return Math.ceil(seconds / day);
};

export const getIsCheckedAndNotMixed = ({
  checked,
  isMixed,
}: AvailabilityActionFormUtility) => checked && !isMixed;

const prepareMembers = (members: BulkAvailabilityMember[]) =>
  members.map((member) => ({
    ...member,
    view: {
      ...member.view,
      checked: getIsChecked(member.view.state),
      isMixed: getIsMixed(member.view.state),
    },
    download: {
      ...member.download,
      checked: getIsChecked(member.download.state),
      isMixed: getIsMixed(member.download.state),
    },
  }));

const getShouldTurnOff = (
  currentValue: BulkAvailabilityState,
  node: Partial<DataRoomNode>,
  key: keyof DataRoomNode,
) =>
  currentValue === AVAILABILITY_STATE.ON &&
  currentValue !== getAvailabilityState(node[key] as boolean);

const getShouldSetMixed = (
  currentValue: BulkAvailabilityState,
  node: Partial<DataRoomNode>,
  key: keyof DataRoomNode,
) =>
  !getIsMixed(currentValue) &&
  currentValue !== getAvailabilityState(node[key] as boolean);

const getNodesProps = (dataRoomNodes: Partial<DataRoomNode>[]) => {
  const first = dataRoomNodes[0];

  let allowSoftSignals = getAvailabilityState(first.allowSoftSignals);
  let blurProtection = getAvailabilityState(first.blurProtection);
  let fileSecurity = first.fileSecurity;
  let expirationDays: typeof first.expirationDays | 'mixed' =
    first.expirationDays;

  let watermarkDiagonal = getAvailabilityState(first.watermarkDiagonal);
  let watermarkHeader = getAvailabilityState(first.watermarkHeader);
  let watermarkFooter = getAvailabilityState(first.watermarkFooter);
  let watermarkFullName = getAvailabilityState(first.watermarkFullName);
  let watermarkEmail = getAvailabilityState(first.watermarkEmail);
  let watermarkIpAddress = getAvailabilityState(first.watermarkIpAddress);

  for (let i = 1; i < dataRoomNodes.length; i++) {
    const node = dataRoomNodes[i];

    if (getShouldTurnOff(allowSoftSignals, node, 'allowSoftSignals')) {
      allowSoftSignals = AVAILABILITY_STATE.OFF;
    }

    if (getShouldTurnOff(blurProtection, node, 'blurProtection')) {
      blurProtection = AVAILABILITY_STATE.OFF;
    }

    if (fileSecurity !== node.fileSecurity) {
      fileSecurity = undefined;
    }

    if (
      expirationDays !== AVAILABILITY_STATE.MIXED &&
      expirationDays !== node.expirationDays
    ) {
      expirationDays = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkDiagonal, node, 'watermarkDiagonal')) {
      watermarkDiagonal = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkHeader, node, 'watermarkHeader')) {
      watermarkHeader = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkFooter, node, 'watermarkFooter')) {
      watermarkFooter = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkFullName, node, 'watermarkFullName')) {
      watermarkFullName = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkEmail, node, 'watermarkEmail')) {
      watermarkEmail = AVAILABILITY_STATE.MIXED;
    }

    if (getShouldSetMixed(watermarkIpAddress, node, 'watermarkIpAddress')) {
      watermarkIpAddress = AVAILABILITY_STATE.MIXED;
    }
  }

  return {
    allowSoftSignals,
    blurProtection,
    fileSecurity,
    expirationDays,
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
  };
};

const isUsersNode = (userId: string, node: Partial<DataRoomNode>) =>
  node.userId === userId;

const getCanDowngrade = (
  role: DataRoomMemberRole,
  userId: string,
  dataRoomNodes: Partial<DataRoomNode>[],
) =>
  !isDataRoomPublisher(role) ||
  !dataRoomNodes.some((n) => isUsersNode(userId, n));

const getDisabled = (
  role: DataRoomMemberRole,
  userId: string,
  dataRoomNodes: Partial<DataRoomNode>[],
) =>
  isDataRoomAdminOrOwner(role) ||
  (isDataRoomPublisher(role) &&
    dataRoomNodes.every((n) => isUsersNode(userId, n)));

const hasAccessToNode = (
  userId: string,
  accessableDataRoomNodesIds: string[],
  dataRoomNode: Partial<DataRoomNode>,
) =>
  isUsersNode(userId, dataRoomNode) ||
  accessableDataRoomNodesIds.includes(dataRoomNode._id);

const getStateForAction = (
  userId: string,
  role: DataRoomMemberRole,
  accessableDataRoomNodesIds: string[],
  dataRoomNodes: Partial<DataRoomNode>[],
) => {
  if (isDataRoomAdminOrOwner(role)) return AVAILABILITY_STATE.ON;

  if (
    dataRoomNodes.some((node) =>
      hasAccessToNode(userId, accessableDataRoomNodesIds, node),
    ) &&
    dataRoomNodes.some(
      (node) => !hasAccessToNode(userId, accessableDataRoomNodesIds, node),
    )
  ) {
    return AVAILABILITY_STATE.MIXED;
  }

  if (
    dataRoomNodes.every((node) =>
      hasAccessToNode(userId, accessableDataRoomNodesIds, node),
    )
  ) {
    return AVAILABILITY_STATE.ON;
  }

  return AVAILABILITY_STATE.OFF;
};

export const prepareMember = (
  dataRoomNodes: Partial<DataRoomNode>[],
  member: DataRoomMember,
  accessConfirmationDays?: number,
) => {
  const {
    _id,
    userId,
    isLead,
    role,
    accessableDataRoomNodesView,
    accessableDataRoomNodesDownload,
    filesAccessExpiredAt,
  } = member || {};

  const disabled = getDisabled(role, userId, dataRoomNodes);
  const canDowngrade = getCanDowngrade(role, userId, dataRoomNodes);

  return {
    _id,
    isLead,
    name: getUserFullNameOrEmail(member),
    view: {
      disabled,
      canDowngrade,
      state: getStateForAction(
        userId,
        role,
        accessableDataRoomNodesView,
        dataRoomNodes,
      ),
    },
    download: {
      disabled,
      canDowngrade,
      state: getStateForAction(
        userId,
        role,
        accessableDataRoomNodesDownload,
        dataRoomNodes,
      ),
    },
    filesAccessExpiredAt,
    fileExpirationDateInDays: getFileExpirationDateInDays(
      filesAccessExpiredAt,
      accessConfirmationDays,
    ),
  };
};

export const prepareBuySideGroups = (
  dataRoomNodes: Partial<DataRoomNode>[],
  buySideGroups: DataRoomBuySideGroup[],
  accessConfirmationDays: number,
) => {
  const preparedGroups = buySideGroups
    .filter(({ dataRoomMembers }) => dataRoomMembers?.length)
    .map(({ name, dataRoomMembers }) => ({
      name,
      dataRoomMembers: dataRoomMembers.map((member) =>
        prepareMember(dataRoomNodes, member, accessConfirmationDays),
      ),
    }));

  const groupsWithSingleMembers = preparedGroups.filter(({ dataRoomMembers }) =>
    isSingleElementArray(dataRoomMembers),
  );
  const groupsWithMultipleMembers = preparedGroups.filter(
    ({ dataRoomMembers }) => !isSingleElementArray(dataRoomMembers),
  );

  return [
    ...groupsWithMultipleMembers,
    {
      name: 'usersInSingleMemberGroups',
      isSingleMemberGroup: true,
      dataRoomMembers: groupsWithSingleMembers.flatMap(
        ({ dataRoomMembers }) => dataRoomMembers,
      ),
    },
  ];
};

export const getGlobalFileExpirationDateInDays = (
  buySideGroups: BulkAvailabilityBuySideMembersForm[],
) =>
  buySideGroups
    .flatMap(({ dataRoomMembers }) => dataRoomMembers)
    .filter(({ view }) => getIsCheckedAndNotMixed(view))
    .sort(
      (a, b) => +a.fileExpirationDateInDays - +b.fileExpirationDateInDays,
    )?.[0]?.fileExpirationDateInDays;

export const prepareResponseForFormData = (
  data: BulkAvailabilityMembersResponse & {
    sellSideMembers: DataRoomMember[];
    buySideGroups: DataRoomBuySideGroup[];
    accessConfirmationDays: number;
  },
) => {
  const {
    dataRoomNodes,
    buySideGroups,
    sellSideMembers,
    accessConfirmationDays,
  } = data || {};

  return {
    fileSettings: getNodesProps(dataRoomNodes),
    sellSideMembers: sellSideMembers.map((member) =>
      prepareMember(dataRoomNodes, member),
    ),
    buySideGroups: prepareBuySideGroups(
      dataRoomNodes,
      buySideGroups,
      accessConfirmationDays,
    ),
  };
};

export const prepareFormData = ({
  fileSettings,
  sellSideMembers,
  buySideGroups,
}: BulkAvailabilityMembers) => {
  const formBuySideGroups = buySideGroups.map(
    ({ name, isSingleMemberGroup, dataRoomMembers }) => ({
      name,
      isSingleMemberGroup,
      dataRoomMembers: prepareMembers(dataRoomMembers),
    }),
  );

  return {
    allowSoftSignals: {
      state: fileSettings.allowSoftSignals,
      checked: getIsChecked(fileSettings.allowSoftSignals),
      isMixed: getIsMixed(fileSettings.allowSoftSignals),
    },
    blurProtection: {
      state: fileSettings.blurProtection,
      checked: getIsChecked(fileSettings.blurProtection),
      isMixed: getIsMixed(fileSettings.blurProtection),
    },
    fileSecurity: fileSettings.fileSecurity,
    expirationDays:
      typeof fileSettings.expirationDays === 'string'
        ? undefined
        : fileSettings.expirationDays || MAX_EXPIRATION_DAYS,
    watermarkDiagonal: {
      state: fileSettings.watermarkDiagonal,
      checked: getIsChecked(fileSettings.watermarkDiagonal),
      isMixed: getIsMixed(fileSettings.watermarkDiagonal),
    },
    watermarkHeader: {
      state: fileSettings.watermarkHeader,
      checked: getIsChecked(fileSettings.watermarkHeader),
      isMixed: getIsMixed(fileSettings.watermarkHeader),
    },
    watermarkFooter: {
      state: fileSettings.watermarkFooter,
      checked: getIsChecked(fileSettings.watermarkFooter),
      isMixed: getIsMixed(fileSettings.watermarkFooter),
    },
    watermarkFullName: {
      state: fileSettings.watermarkFullName,
      checked: getIsChecked(fileSettings.watermarkFullName),
      isMixed: getIsMixed(fileSettings.watermarkFullName),
    },
    watermarkEmail: {
      state: fileSettings.watermarkEmail,
      checked: getIsChecked(fileSettings.watermarkEmail),
      isMixed: getIsMixed(fileSettings.watermarkEmail),
    },
    watermarkIpAddress: {
      state: fileSettings.watermarkIpAddress,
      checked: getIsChecked(fileSettings.watermarkIpAddress),
      isMixed: getIsMixed(fileSettings.watermarkIpAddress),
    },
    sellSideMembers: prepareMembers(sellSideMembers),
    buySideGroups: formBuySideGroups,
    fileExpirationDateInDays:
      getGlobalFileExpirationDateInDays(formBuySideGroups),
  };
};

const hasChanged = (
  newData: AvailabilityActionFormUtility,
  oldData: AvailabilityActionFormUtility,
) => newData.checked !== oldData.checked || newData.isMixed !== oldData.isMixed;

const joinMembers = (
  sellSideMembers: BulkFormAvailabilityMember[],
  buySideGroups: { dataRoomMembers: BulkFormAvailabilityMember[] }[],
) => [
  ...sellSideMembers,
  ...buySideGroups.flatMap(({ dataRoomMembers }) => dataRoomMembers),
];

export const processDataForUpdatedValues = (
  data: BulkAvailabilityMembersForm,
  originalData: BulkAvailabilityMembersForm,
) => {
  const newData = {} as BulkAvailabilityUpdateMembers;

  if (hasChanged(data.allowSoftSignals, originalData.allowSoftSignals))
    newData.allowSoftSignals = data.allowSoftSignals.checked;

  if (hasChanged(data.blurProtection, originalData.blurProtection))
    newData.blurProtection = data.blurProtection.checked;

  if (data.fileSecurity !== originalData.fileSecurity)
    newData.fileSecurity = data.fileSecurity;

  if (data.expirationDays !== originalData.expirationDays)
    newData.expirationDays = +(data.expirationDays || MAX_EXPIRATION_DAYS);

  if (hasChanged(data.watermarkDiagonal, originalData.watermarkDiagonal))
    newData.watermarkDiagonal = data.watermarkDiagonal.checked;

  if (hasChanged(data.watermarkHeader, originalData.watermarkHeader))
    newData.watermarkHeader = data.watermarkHeader.checked;

  if (hasChanged(data.watermarkFooter, originalData.watermarkFooter))
    newData.watermarkFooter = data.watermarkFooter.checked;

  if (hasChanged(data.watermarkFullName, originalData.watermarkFullName))
    newData.watermarkFullName = data.watermarkFullName.checked;

  if (hasChanged(data.watermarkEmail, originalData.watermarkEmail))
    newData.watermarkEmail = data.watermarkEmail.checked;

  if (hasChanged(data.watermarkIpAddress, originalData.watermarkIpAddress))
    newData.watermarkIpAddress = data.watermarkIpAddress.checked;

  const dataMembers = joinMembers(data.sellSideMembers, data.buySideGroups);
  const originalMembers = joinMembers(
    originalData.sellSideMembers,
    originalData.buySideGroups,
  );

  newData.changedPermissions = dataMembers.reduce((acc, el) => {
    const originalElement = originalMembers.find(({ _id }) => _id === el._id);

    const hasChangedView = hasChanged(el.view, originalElement.view);
    const hasChangedDownload = hasChanged(
      el.download,
      originalElement.download,
    );
    const hasChangedAccessCycle =
      getIsCheckedAndNotMixed(el.view) &&
      el.fileExpirationDateInDays &&
      +el.fileExpirationDateInDays !==
        +originalElement.fileExpirationDateInDays;

    if (!hasChangedView && !hasChangedDownload && !hasChangedAccessCycle)
      return acc;

    return [
      ...acc,
      {
        dataRoomMemberId: el._id,
        ...(hasChangedView && { enableView: el.view.checked }),
        ...(hasChangedDownload && { enableDownload: el.download.checked }),
        ...(hasChangedAccessCycle && {
          filesAccessExpiredAt: addDaysToDate(+el.fileExpirationDateInDays),
        }),
      },
    ];
  }, []);

  return newData;
};

export const getNodeCurrentState = (
  node: DataRoomNode,
  checkedFilesIds: string[],
) => {
  const isFile = getIsNodeFile(node);

  if (isFile) {
    if (checkedFilesIds.includes(node._id)) {
      return AVAILABILITY_STATE.ON;
    }

    return AVAILABILITY_STATE.OFF;
  }

  const shareableChildrenIds = getShareableChildIds(node);

  if (!checkedFilesIds.length || !shareableChildrenIds.length)
    return AVAILABILITY_STATE.OFF;

  if (shareableChildrenIds.every((id) => checkedFilesIds.includes(id))) {
    return AVAILABILITY_STATE.ON;
  }

  if (shareableChildrenIds.some((id) => checkedFilesIds.includes(id))) {
    return AVAILABILITY_STATE.MIXED;
  }

  return AVAILABILITY_STATE.OFF;
};
